import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError, formatPrice, whatsappLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import EMICalc from "@/components/EMICalc";
import {
  Star,
  MapPin,
  Phone,
  MessageCircle,
  ShieldCheck,
  Camera,
  Eye,
  Users,
  Calendar,
  Home,
  Compass,
  Award,
  Wallet,
  IndianRupee,
  ArrowRight,
  Building2,
  Loader2,
} from "lucide-react";

const RATING_DIMS = [
  { key: "overall", label: "Overall", icon: Star },
  { key: "builder", label: "Builder reputation", icon: Award },
  { key: "location", label: "Location & connectivity", icon: Compass },
  { key: "quality", label: "Construction quality", icon: Home },
  { key: "value", label: "Value for money", icon: Wallet },
];

function StarBar({ value = 0 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-3.5 h-3.5 ${n <= Math.round(value) ? "fill-gold text-gold" : "text-neutral-300"}`}
        />
      ))}
    </div>
  );
}

function StarInput({ value, onChange, size = "md" }) {
  const cls = size === "lg" ? "w-7 h-7" : "w-5 h-5";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`${n <= value ? "text-gold" : "text-neutral-300 hover:text-gold/60"} transition-colors`}
          aria-label={`${n} stars`}
        >
          <Star className={`${cls} ${n <= value ? "fill-gold" : ""}`} />
        </button>
      ))}
    </div>
  );
}

export default function SiteLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [myReview, setMyReview] = useState(null);

  const refreshMyReview = async () => {
    if (!user || (user.role !== "buyer" && user.role !== "admin")) {
      setMyReview(null);
      return;
    }
    try {
      const r = await api.get(`/site/${slug}/my-review`);
      setMyReview(r.data.review || null);
    } catch {
      setMyReview(null);
    }
  };

  const handleWriteReview = () => {
    if (!user) {
      toast.info("Please sign in as a buyer to post a review");
      navigate(`/login?next=${encodeURIComponent(location.pathname + "#reviews")}`);
      return;
    }
    if (!(user.role === "buyer" || user.role === "admin")) {
      toast.error("Only buyer accounts can post reviews");
      return;
    }
    setReviewOpen(true);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d] = await Promise.all([
          api.get(`/site/${slug}`),
          api.post(`/site/${slug}/view`).catch(() => null),
        ]);
        setData(d.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    refreshMyReview();
  }, [slug, user?.id]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-cream">
        <Loader2 className="w-6 h-6 animate-spin text-forest" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-cream text-center px-6" data-testid="site-not-found">
        <div className="text-[11px] uppercase tracking-[0.24em] text-gold font-bold">404</div>
        <h1 className="mt-3 font-heading font-extrabold text-3xl text-forest">Landing page not found</h1>
        <p className="text-sm text-muted-foreground mt-2">The project you&apos;re looking for isn&apos;t published.</p>
        <Link to="/projects" className="mt-6 inline-flex items-center gap-2 text-forest font-medium hover:text-gold-hover">
          Browse all projects <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const { project, rating_summary, reviews, stats } = data;
  const hero = project.hero_image || project.gallery?.[0] || "";
  const priceRange = project.price_to && project.price_to !== project.price_from
    ? `${formatPrice(project.price_from)} – ${formatPrice(project.price_to)}`
    : formatPrice(project.price_from);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const waText = encodeURIComponent(`Hi, I saw ${project.name} on Our First Realty (${shareUrl}) — could you share more details?`);

  return (
    <div className="bg-cream min-h-screen" data-testid="site-landing">
      {/* HERO */}
      <section className="relative">
        <div className="h-[380px] md:h-[440px] overflow-hidden bg-forest">
          {hero && <img src={hero} alt={project.name} className="w-full h-full object-cover opacity-90" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-6 -mt-24 relative z-10">
          <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] p-6 md:p-8 border border-border">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {project.verified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-forest/10 border border-forest/20 text-forest text-[11px] font-bold px-2.5 py-1 uppercase tracking-wider">
                      <ShieldCheck className="w-3 h-3" /> OFR Verified
                    </span>
                  )}
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {project.project_type}
                  </span>
                </div>
                <h1 className="mt-2 font-heading font-extrabold text-3xl md:text-4xl text-forest tracking-tight leading-tight" data-testid="project-name">
                  {project.name}
                </h1>
                <div className="mt-2 text-sm text-muted-foreground flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-gold" /> {project.location}
                  {project.city && `, ${project.city}`}
                </div>
                <div className="mt-4 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-1.5" data-testid="rating-header">
                    <span className="text-3xl font-heading font-extrabold text-forest">
                      {rating_summary.avg_overall ? rating_summary.avg_overall.toFixed(1) : "—"}
                    </span>
                    <div className="flex flex-col">
                      <StarBar value={rating_summary.avg_overall || 0} />
                      <span className="text-[10px] text-muted-foreground mt-0.5">
                        {rating_summary.count} review{rating_summary.count === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                  <div className="h-8 w-px bg-border" />
                  <div className="text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {stats.page_views.toLocaleString("en-IN")} page views</span>
                    <span className="mx-2">·</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {stats.site_visits_scheduled} site visits scheduled</span>
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Starting at</div>
                <div className="mt-1 font-heading font-extrabold text-2xl md:text-3xl text-forest flex items-center gap-0.5 justify-end">
                  <IndianRupee className="w-5 h-5 -mt-0.5" />{priceRange.replace("₹", "")}
                </div>
                {project.bhk_options?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">{project.bhk_options.join(" · ")}</div>
                )}
              </div>
            </div>
            {/* Action buttons row — Google Business style */}
            <div className="mt-6 grid grid-cols-4 gap-2 text-center">
              <a href={`tel:+919010903620`} data-testid="cta-call" className="rounded-xl border border-border py-3 px-2 hover:bg-forest hover:text-white transition-colors group">
                <Phone className="w-5 h-5 mx-auto text-gold group-hover:text-gold" />
                <div className="text-[11px] mt-1 font-medium">Call</div>
              </a>
              <a href={whatsappLink(waText)} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="rounded-xl border border-border py-3 px-2 hover:bg-whatsapp hover:text-white transition-colors group">
                <MessageCircle className="w-5 h-5 mx-auto text-whatsapp" />
                <div className="text-[11px] mt-1 font-medium">WhatsApp</div>
              </a>
              <a
                href={project.latitude && project.longitude ? `https://www.google.com/maps/dir/?api=1&destination=${project.latitude},${project.longitude}` : `https://www.google.com/maps/search/${encodeURIComponent(project.name + " " + (project.address || project.location))}`}
                target="_blank" rel="noreferrer" data-testid="cta-directions"
                className="rounded-xl border border-border py-3 px-2 hover:bg-forest hover:text-white transition-colors group"
              >
                <MapPin className="w-5 h-5 mx-auto text-gold" />
                <div className="text-[11px] mt-1 font-medium">Directions</div>
              </a>
              <Link to={`/projects/${project.id}`} data-testid="cta-website" className="rounded-xl border border-border py-3 px-2 hover:bg-forest hover:text-white transition-colors group">
                <Building2 className="w-5 h-5 mx-auto text-gold" />
                <div className="text-[11px] mt-1 font-medium">Full page</div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TABS */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mt-8">
        <div className="border-b border-border">
          {["overview", "reviews", "photos"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-4 py-3 text-sm font-medium capitalize border-b-2 transition-colors ${
                tab === t ? "border-gold text-forest" : "border-transparent text-muted-foreground hover:text-forest"
              }`}
            >
              {t}
              {t === "reviews" && rating_summary.count > 0 && (
                <span className="ml-1.5 text-[10px] bg-forest/10 rounded-full px-1.5 py-0.5 text-forest">{rating_summary.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* TAB CONTENT */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {tab === "overview" && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-border bg-white p-6">
                <h2 className="font-heading font-bold text-forest text-lg">About this project</h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {project.description || "This project is verified by Our First Realty. Contact us for details on unit availability, current pricing, and site visit scheduling."}
                </p>
                {project.highlights?.length > 0 && (
                  <div className="mt-5 grid sm:grid-cols-2 gap-2">
                    {project.highlights.map((h, i) => (
                      <div key={i} className="text-xs text-forest flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {h}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rating breakdown */}
              <div className="rounded-2xl border border-border bg-white p-6" data-testid="rating-breakdown">
                <h2 className="font-heading font-bold text-forest text-lg">What visitors say</h2>
                <div className="mt-4 space-y-3">
                  {RATING_DIMS.map((d) => {
                    const v = rating_summary[`avg_${d.key}`] || 0;
                    return (
                      <div key={d.key} className="grid grid-cols-[140px_1fr_60px] items-center gap-3">
                        <div className="text-xs text-forest flex items-center gap-1.5">
                          <d.icon className="w-3.5 h-3.5 text-gold" /> {d.label}
                        </div>
                        <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-gold to-gold-hover" style={{ width: `${(v / 5) * 100}%` }} />
                        </div>
                        <div className="text-xs font-semibold text-forest text-right">{v ? v.toFixed(1) : "—"} / 5</div>
                      </div>
                    );
                  })}
                </div>
                <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="write-review-btn" onClick={handleWriteReview} className="mt-5 bg-forest hover:bg-forest-hover text-white rounded-full">
                      {myReview ? "Edit your review" : "Write a review"}
                    </Button>
                  </DialogTrigger>
                  <ReviewFormDialog
                    slug={slug}
                    projectName={project.name}
                    defaultName={user?.name || ""}
                    defaultPhone={user?.phone || ""}
                    existingReview={myReview}
                    onDone={() => { setReviewOpen(false); refreshMyReview(); }}
                  />
                </Dialog>
              </div>

              {/* EMI mini */}
              <div className="rounded-2xl border border-border bg-white p-6">
                <h2 className="font-heading font-bold text-forest text-lg mb-4">EMI calculator</h2>
                <EMICalc defaultPrice={project.price_from} />
              </div>
            </div>

            {/* Right — quick facts */}
            <aside className="space-y-4">
              <div className="rounded-2xl border border-border bg-white p-5">
                <div className="text-[11px] uppercase tracking-widest text-gold font-bold mb-3">Quick facts</div>
                <dl className="text-sm text-forest space-y-2.5">
                  <Row label="Builder" value={project.builder_name} />
                  <Row label="Type" value={<span className="capitalize">{project.project_type}</span>} />
                  <Row label="RERA #" value={project.rera_number || "—"} />
                  <Row label="Units total" value={project.total_units || "—"} />
                  <Row label="Available" value={project.available_units || "—"} />
                  <Row label="Possession" value={project.possession_date || "—"} />
                </dl>
              </div>
              {project.amenities?.length > 0 && (
                <div className="rounded-2xl border border-border bg-white p-5">
                  <div className="text-[11px] uppercase tracking-widest text-gold font-bold mb-3">Amenities</div>
                  <div className="flex flex-wrap gap-1.5">
                    {project.amenities.map((a) => (
                      <span key={a} className="text-[11px] px-2 py-1 rounded-full bg-cream border border-border text-forest">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              {project.latitude && project.longitude && (
                <div className="rounded-2xl border border-border bg-white overflow-hidden">
                  <iframe
                    title="map"
                    src={`https://www.google.com/maps?q=${project.latitude},${project.longitude}&z=15&output=embed`}
                    className="w-full aspect-video"
                    loading="lazy"
                  />
                  <div className="p-3 text-xs text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 inline-block mr-1 text-gold" />
                    {project.address || project.location}
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}

        {tab === "reviews" && (
          <div className="max-w-3xl mx-auto space-y-4">
            {myReview && !myReview.approved && (
              <div data-testid="my-review-pending" className="rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-xs text-forest/90">
                Your review is awaiting moderation. Feel free to edit it any time — the latest version is what our team will approve.
              </div>
            )}
            {reviews.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center" data-testid="empty-reviews">
                <Star className="w-10 h-10 text-gold mx-auto" />
                <div className="mt-3 font-heading font-bold text-forest">{myReview ? "Your review will appear here soon" : "Be the first to review"}</div>
                <p className="text-sm text-muted-foreground mt-1">{myReview ? "Once our team approves it, your review will be visible to other buyers." : `Share your experience visiting ${project.name}. Your review will appear here after our team approves it.`}</p>
                <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                  <Button data-testid="write-review-btn-2" onClick={handleWriteReview} className="mt-5 bg-forest hover:bg-forest-hover text-white rounded-full">{myReview ? "Edit your review" : "Write a review"}</Button>
                  <ReviewFormDialog slug={slug} projectName={project.name} defaultName={user?.name || ""} defaultPhone={user?.phone || ""} existingReview={myReview} onDone={() => { setReviewOpen(false); refreshMyReview(); }} />
                </Dialog>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">{reviews.length} review{reviews.length === 1 ? "" : "s"}</div>
                  <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                    <Button data-testid="write-review-btn-3" onClick={handleWriteReview} size="sm" className="bg-forest hover:bg-forest-hover text-white rounded-full">{myReview ? "Edit your review" : "Write a review"}</Button>
                    <ReviewFormDialog slug={slug} projectName={project.name} defaultName={user?.name || ""} defaultPhone={user?.phone || ""} existingReview={myReview} onDone={() => { setReviewOpen(false); refreshMyReview(); }} />
                  </Dialog>
                </div>
                {reviews.map((r) => (
                  <ReviewCard key={r.id} review={r} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "photos" && (
          <div>
            {(!project.gallery || project.gallery.length === 0) && !project.hero_image ? (
              <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
                <Camera className="w-10 h-10 text-gold mx-auto" />
                <div className="mt-3 font-heading font-bold text-forest">No photos yet</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[project.hero_image, ...(project.gallery || [])].filter(Boolean).map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden bg-white border border-border">
                    <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-right">{value}</dd>
    </div>
  );
}

function ReviewCard({ review }) {
  const date = review.created_at ? new Date(review.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "";
  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-heading font-bold text-forest">{review.reviewer_name}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5"><Calendar className="w-3 h-3 inline mr-1" />{date}</div>
        </div>
        <div className="text-right">
          <StarBar value={review.ratings?.overall || 0} />
          <div className="text-[10px] text-muted-foreground mt-0.5">{review.ratings?.overall}/5</div>
        </div>
      </div>
      {review.comment && <p className="mt-3 text-sm text-forest/90 leading-relaxed">{review.comment}</p>}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {RATING_DIMS.filter((d) => d.key !== "overall").map((d) => (
          review.ratings?.[d.key] ? (
            <div key={d.key} className="inline-flex items-center gap-1">
              <d.icon className="w-3 h-3 text-gold" /> {d.label}: {review.ratings[d.key]}/5
            </div>
          ) : null
        ))}
      </div>
    </div>
  );
}

function ReviewFormDialog({ slug, projectName, defaultName = "", defaultPhone = "", existingReview = null, onDone }) {
  const isEdit = !!existingReview;
  const [name, setName] = useState(existingReview?.reviewer_name || defaultName);
  const [phone, setPhone] = useState(existingReview?.reviewer_phone || defaultPhone);
  const [comment, setComment] = useState(existingReview?.comment || "");
  const [ratings, setRatings] = useState({
    overall: existingReview?.ratings?.overall || 0,
    builder: existingReview?.ratings?.builder || 0,
    location: existingReview?.ratings?.location || 0,
    quality: existingReview?.ratings?.quality || 0,
    value: existingReview?.ratings?.value || 0,
  });
  const [submitting, setSubmitting] = useState(false);

  // Sync fields whenever the dialog is opened for a different review
  useEffect(() => {
    setName(existingReview?.reviewer_name || defaultName);
    setPhone(existingReview?.reviewer_phone || defaultPhone);
    setComment(existingReview?.comment || "");
    setRatings({
      overall: existingReview?.ratings?.overall || 0,
      builder: existingReview?.ratings?.builder || 0,
      location: existingReview?.ratings?.location || 0,
      quality: existingReview?.ratings?.quality || 0,
      value: existingReview?.ratings?.value || 0,
    });
  }, [existingReview?.id, defaultName, defaultPhone]);

  const setDim = (k, v) => setRatings((r) => ({ ...r, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name");
    if (!ratings.overall) return toast.error("Please give an overall star rating");
    setSubmitting(true);
    try {
      const cleanRatings = Object.fromEntries(Object.entries(ratings).filter(([, v]) => v > 0));
      const payload = {
        reviewer_name: name.trim(),
        reviewer_phone: phone.trim(),
        ratings: cleanRatings,
        comment: comment.trim(),
      };
      const r = isEdit
        ? await api.put(`/site/${slug}/reviews/${existingReview.id}`, payload)
        : await api.post(`/site/${slug}/reviews`, payload);
      toast.success(r.data.message || (isEdit ? "Your review has been updated." : "Thanks — your review is pending approval."));
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-heading text-forest">{isEdit ? "Edit review" : "Review"} · {projectName}</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} data-testid="review-form" className="grid gap-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="rv-name" className="text-xs">Your name *</Label>
            <Input id="rv-name" data-testid="rv-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="rv-phone" className="text-xs">Phone (optional)</Label>
            <Input id="rv-phone" data-testid="rv-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 …" />
          </div>
        </div>
        <div className="rounded-lg bg-cream/60 border border-border p-4">
          <div className="text-xs uppercase tracking-wider text-gold font-bold mb-3">Rate this project</div>
          <div className="space-y-2.5">
            {RATING_DIMS.map((d) => (
              <div key={d.key} className="flex items-center justify-between gap-3">
                <div className="text-sm text-forest flex items-center gap-1.5">
                  <d.icon className="w-3.5 h-3.5 text-gold" /> {d.label} {d.key === "overall" && <span className="text-red-500">*</span>}
                </div>
                <StarInput value={ratings[d.key]} onChange={(v) => setDim(d.key, v)} size={d.key === "overall" ? "lg" : "md"} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="rv-comment" className="text-xs">Comment (optional)</Label>
          <Textarea id="rv-comment" data-testid="rv-comment" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What worked well? What could be better?" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={submitting} data-testid="submit-review" className="bg-forest hover:bg-forest-hover text-white">
            {submitting ? "Sending…" : isEdit ? "Save changes" : "Submit review"}
          </Button>
        </DialogFooter>
        <p className="text-[10px] text-muted-foreground">{isEdit ? "Edited reviews go back through a quick moderation step before appearing publicly." : "Reviews go through a quick moderation step before appearing publicly."}</p>
      </form>
    </DialogContent>
  );
}
